// Socket syntax layers used in this tool (PoE1; PoE2 has count only — no links, groups, or colours):
//
// 1. UI / model — RGBWX (+ optional T in inputs). No L-prefix.
//    Advanced Conditions, Item Designer, My Equipment. X = wildcard; T = transparent (display-only).
// 2. Label viewer — optional L + colours, or game clipboard text.
//    L = fully linked single group (e.g. LBRRR). Clipboard: dashes = links, spaces = groups (e.g. B-B-G-B G).
//    Built by getGameItemSocketsDisplay; rendered by sockets-display.
// 3. Filter file — SocketGroup / Sockets / LinkedSockets (e.g. SocketGroup 4RRG, Sockets >= 5).
//    No X or L. UI X becomes a size prefix via formatSocketGroup (RRGX → 4RRG).
// 4. In-game clipboard — same as label clipboard form (W-W W). PoE1 uses W; PoE2 uses S (mapped to W on parse).
//    Stored on GameItem.socketsDisplay when pasting items.
//
// Matching below uses filter tokens (3) against item groups from socketsDisplay / socketGroup / counts.

import { ConditionOperator } from '../models/parsing-models';
import { GameItem } from '../models/game-item.models';

export interface SocketSpec {
  size?: number;
  colours: string; // R/G/B/W/A/D/X letters only
}

// Parses one SocketGroup/Sockets token such as "3BB", "RGB", "5", or "WWW".
export function parseSocketSpec(token: string): SocketSpec {
  const trimmed = token.trim().toUpperCase();
  const match = trimmed.match(/^(\d+)?([RGBWADX]*)$/);
  if (!match) {
    return { colours: '' };
  }
  const size = match[1] ? parseInt(match[1], 10) : undefined;
  return { size, colours: match[2] || '' };
}

export function getLinkedSocketGroups(item: GameItem): string[] {
  if (item.socketsDisplay) {
    return item.socketsDisplay
      .trim()
      .split(/\s+/)
      .map(group => normalizeSocketColours(group.replace(/-/g, '')))
      .filter(group => group.length > 0);
  }
  if (item.socketGroup) {
    return [normalizeSocketColours(item.socketGroup)];
  }
  if (item.linkedSockets && item.linkedSockets > 0) {
    return ['X'.repeat(item.linkedSockets)];
  }
  return [];
}

export function getAllSocketColours(item: GameItem): string {
  const groups = getLinkedSocketGroups(item);
  if (groups.length > 0) {
    return groups.join('');
  }
  if (item.sockets && item.sockets > 0) {
    return 'X'.repeat(item.sockets);
  }
  return '';
}

function normalizeSocketColours(colours: string): string {
  return colours
    .toUpperCase()
    .split('')
    .map(char => {
      if (char === 'R' || char === 'G' || char === 'B' || char === 'W' || char === 'A' || char === 'D') {
        return char;
      }
      if (char === 'S') {
        return 'W';
      }
      return 'X';
    })
    .join('');
}

// PoE 3.29+: white is the default socket and does not satisfy R/G/B requirements
// (chromatic recipe and SocketGroup/Sockets colour matching need actual colours).
export function coloursMatch(available: string, required: string): boolean {
  if (!required) {
    return true;
  }

  const availableCounts = countColours(available);
  const requiredCounts = countColours(required);

  for (const colour of ['R', 'G', 'B', 'W', 'A', 'D'] as const) {
    const needed = requiredCounts[colour] || 0;
    if ((availableCounts[colour] || 0) < needed) {
      return false;
    }
  }

  return true;
}

function countColours(colours: string): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const char of colours) {
    counts[char] = (counts[char] || 0) + 1;
  }
  return counts;
}

function sizeMatches(actualSize: number, requiredSize: number | undefined, operator: ConditionOperator): boolean {
  if (requiredSize === undefined) {
    return true;
  }
  switch (operator) {
    case ConditionOperator.GreaterThan:
      return actualSize >= requiredSize;
    case ConditionOperator.LessThan:
      return actualSize <= requiredSize;
    case ConditionOperator.Equals:
    case ConditionOperator.Like:
    default:
      return actualSize === requiredSize;
  }
}

export function matchesSocketSpec(pool: string, spec: SocketSpec, operator: ConditionOperator): boolean {
  if (spec.size === undefined && !spec.colours) {
    return false;
  }

  // Pure numeric: compare pool size only.
  if (spec.size !== undefined && !spec.colours) {
    return sizeMatches(pool.length, spec.size, operator);
  }

  // Colour-only W with less-than operators: max white sockets.
  // Parser maps both < and <= to LessThan, so:
  // - "W" → whiteCount < 1 (generator uses Sockets < W for zero whites)
  // - "WW"+ → whiteCount <= length (generator uses Sockets <= WW)
  if (spec.size === undefined && /^W+$/.test(spec.colours) && operator === ConditionOperator.LessThan) {
    const whiteCount = (pool.match(/W/g) || []).length;
    if (spec.colours.length === 1) {
      return whiteCount < 1;
    }
    return whiteCount <= spec.colours.length;
  }

  if (!sizeMatches(pool.length, spec.size, operator)) {
    return false;
  }

  return coloursMatch(pool, spec.colours);
}

export function evaluateSocketGroupCondition(
  condition: { operator: ConditionOperator; value: string },
  item: GameItem
): boolean {
  const alternatives = condition.value.trim().split(/\s+/).filter(Boolean);
  if (alternatives.length === 0) {
    return false;
  }

  const groups = getLinkedSocketGroups(item);
  if (groups.length === 0) {
    return alternatives.some(token => {
      const spec = parseSocketSpec(token);
      return spec.size === 0 && !spec.colours;
    });
  }

  return alternatives.some(token => {
    const spec = parseSocketSpec(token);
    return groups.some(group => matchesSocketSpec(group, spec, condition.operator));
  });
}

export function evaluateSocketsCondition(
  condition: { operator: ConditionOperator; value: string },
  item: GameItem
): boolean {
  const alternatives = condition.value.trim().split(/\s+/).filter(Boolean);
  if (alternatives.length === 0) {
    return false;
  }

  const pool = getAllSocketColours(item);
  const totalSockets = item.sockets ?? pool.length;

  return alternatives.some(token => {
    const spec = parseSocketSpec(token);

    // Pure numeric uses the sockets count (supports items with count but no colours).
    if (spec.size !== undefined && !spec.colours) {
      return sizeMatches(totalSockets, spec.size, condition.operator);
    }

    return matchesSocketSpec(pool, spec, condition.operator);
  });
}
